import { useState, useCallback } from 'react';

const STORAGE_KEY = 'hairx_consent_v1';

export interface ConsentState {
  essential: true;
  performance: boolean;
  analytics: boolean;
  advertising: boolean;
  date: string;
}

function readStorage(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

function writeStorage(state: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('hairx:consent-updated', { detail: state }));
  } catch {
    // localStorage unavailable
  }
}

function buildConsent(performance: boolean, analytics: boolean, advertising: boolean): ConsentState {
  return { essential: true, performance, analytics, advertising, date: new Date().toISOString() };
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentState | null>(readStorage);

  const hasDecided = consent !== null;

  const acceptAll = useCallback(() => {
    const state = buildConsent(true, true, true);
    writeStorage(state);
    setConsent(state);
  }, []);

  const rejectAll = useCallback(() => {
    const state = buildConsent(false, false, false);
    writeStorage(state);
    setConsent(state);
  }, []);

  const updateConsent = useCallback((updates: Partial<Omit<ConsentState, 'essential' | 'date'>>) => {
    const current = consent ?? buildConsent(false, false, false);
    const state: ConsentState = { ...current, ...updates, essential: true, date: new Date().toISOString() };
    writeStorage(state);
    setConsent(state);
  }, [consent]);

  return { consent, hasDecided, acceptAll, rejectAll, updateConsent };
}
